import {
  LayoutDashboard,
  Building2,
  GraduationCap,
  Factory,
  Inbox,
  ListFilter,
  Target,
  Newspaper,
  Mail,
  Radio,
  Crosshair,
  CalendarRange,
  BarChart3,
  Sparkles,
  Users,
  CheckSquare,
  BookOpen,
  Library,
  Globe2,
  LogOut,
  LucideIcon,
  ChevronRight,
} from "lucide-react";
import carrLogo from "@/assets/carr-logo.png";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";

type MenuItem = { title: string; url: string; icon: LucideIcon };

const navSections: { label: string | null; items: MenuItem[] }[] = [
  {
    label: null,
    items: [
      { title: "Dashboard", url: "/", icon: LayoutDashboard },
    ],
  },
  {
    label: "Markets",
    items: [
      { title: "Therapeutic Developers", url: "/accounts", icon: Building2 },
      { title: "Service Providers", url: "/service-providers", icon: Factory },
      { title: "Academic Market", url: "/academic-market", icon: GraduationCap },
      { title: "Needs Review", url: "/needs-review", icon: Inbox },
      { title: "List Builder", url: "/list-builder", icon: ListFilter },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { title: "News", url: "/news", icon: Newspaper },
      { title: "Newsletter", url: "/newsletter", icon: Mail },
      { title: "Social Listening", url: "/company-tracking", icon: Radio },
      { title: "Events", url: "/events", icon: CalendarRange },
      { title: "Research Library", url: "/research-library", icon: Library },
      { title: "Market Intel", url: "/market-intel", icon: Globe2 },
    ],
  },
  {
    label: "Operations",
    items: [
      { title: "Target Universe", url: "/target-universe", icon: Crosshair },
      { title: "Campaign Hub", url: "/campaigns/hub", icon: Target },
      { title: "Campaign Tasks", url: "/campaigns/tasks", icon: CheckSquare },
      { title: "Content Studio", url: "/content-studio", icon: Sparkles },
      { title: "Team Profiles", url: "/team-profiles", icon: Users },
    ],
  },
  {
    label: "Analytics",
    items: [
      { title: "Metrics", url: "/metrics", icon: BarChart3 },
    ],
  },
  {
    label: null,
    items: [
      { title: "User Guide", url: "/user-guide", icon: BookOpen },
    ],
  },
];

export function AppSidebar() {
  const { userProfile, signOut } = useAuth();

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <Sidebar className="border-r-0">
      <SidebarHeader className="px-4 py-5">
        <div className="flex flex-col items-start gap-1">
          <img src={carrLogo} alt="CARR Biosystems" className="h-11 w-auto object-contain" />
          <p className="text-xs text-sidebar-foreground/60 pl-0.5">Intelligence Platform</p>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {navSections.map((section, idx) => {
          if (!section.label) {
            // Dashboard — no collapsible wrapper
            return (
              <SidebarGroup key={idx}>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {section.items.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild>
                          <NavLink
                            to={item.url}
                            end={item.url === "/"}
                            className={({ isActive }) =>
                              isActive
                                ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                                : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                            }
                          >
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            );
          }

          return (
            <Collapsible key={idx} defaultOpen={false} className="group/collapsible">
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className="flex w-full items-center justify-between">
                    {section.label}
                    <ChevronRight className="h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {section.items.map((item) => (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton asChild>
                            <NavLink
                              to={item.url}
                              end={item.url === "/"}
                              className={({ isActive }) =>
                                isActive
                                  ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                                  : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                              }
                            >
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          );
        })}
      </SidebarContent>
      <SidebarFooter className="p-4 border-t border-sidebar-border">
        {userProfile && (
          <div className="space-y-3">
            <div className="text-xs">
              <p className="font-medium text-sidebar-foreground truncate">
                {userProfile.full_name?.trim() || userProfile.email}
              </p>
              <p className="text-sidebar-foreground/60 truncate">
                {userProfile.email}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="w-full justify-start text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/20"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
